import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError, money, imgUrl, telHref, smsHref } from "@/lib/api";
import { CAR_MAKES, MODELS_BY_MAKE, YEARS } from "@/lib/vehicles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Calendar, Wrench, Percent, Car, Tag, Image, Star, Ban, Clock, Settings as Cog,
  LogOut, Plus, Trash2, Pencil, Menu, X, Users, LayoutGrid, Layers, UploadCloud,
  Phone, MessageSquare,
} from "lucide-react";
function AutoField({ value, onChange, options, placeholder, testid }) {
  const [open, setOpen] = useState(false);
  const q = (value || "").toLowerCase();
  const filtered = (options || []).filter((o) => o.toLowerCase().includes(q)).slice(0, 8);
  return (
    <div className="relative">
      <Input data-testid={testid} autoComplete="off" className="rounded-lg bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 mt-1 h-11"
        value={value} placeholder={placeholder}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} />
      {open && filtered.length > 0 && (
        <div className="absolute z-[60] left-0 right-0 bg-white border border-slate-200 shadow-lg max-h-56 overflow-auto rounded-lg">
          {filtered.map((o) => (
            <button type="button" key={o} className="block w-full text-left px-3 py-2 text-sm text-slate-900 hover:bg-slate-100"
              onMouseDown={(e) => { e.preventDefault(); onChange(o); setOpen(false); }}>{o}</button>
          ))}
        </div>
      )}
    </div>
  );
}



const NAV = [
  { key: "appointments", label: "Appointments", icon: Calendar },
  { key: "calendar", label: "Calendar", icon: LayoutGrid },
  { key: "customers", label: "Customers", icon: Users },
  { key: "services", label: "Services", icon: Wrench },
  { key: "films", label: "Film Types", icon: Layers },
  { key: "percentages", label: "Tint %", icon: Percent },
  { key: "vehicle-types", label: "Vehicle Types", icon: Car },
  { key: "promotions", label: "Promotions", icon: Tag },
  { key: "gallery", label: "Gallery", icon: Image },
  { key: "reviews", label: "Reviews", icon: Star },
  { key: "blocked", label: "Availability", icon: Ban },
  { key: "hours", label: "Business Hours", icon: Clock },
  { key: "settings", label: "Settings", icon: Cog },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [section, setSection] = useState("appointments");
  const [ready, setReady] = useState(false);
  const [navOpen, setNavOpen] = useState(false);

  useEffect(() => {
    if (!localStorage.getItem("wm_admin_token")) { navigate("/admin/login"); return; }
    api.get("/auth/me").then(() => setReady(true)).catch(() => {
      localStorage.removeItem("wm_admin_token");
      navigate("/admin/login");
    });
  }, [navigate]);

  const logout = () => { localStorage.removeItem("wm_admin_token"); navigate("/admin/login"); };

  if (!ready) return <div className="min-h-screen bg-black grid place-items-center text-muted-foreground">Loading…</div>;

  return (
    <div className="min-h-screen bg-black flex">
      {/* Sidebar */}
      <aside className={`fixed lg:static z-50 inset-y-0 left-0 w-64 bg-[#0b0b0b] border-r border-white/10 flex flex-col transition-transform ${navOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"}`}>
        <div className="h-16 flex items-center gap-2 px-5 border-b border-white/10">
          <span className="w-8 h-8 grid place-items-center bg-primary text-primary-foreground font-head font-black text-sm">WM</span>
          <span className="font-head font-extrabold uppercase tracking-tight text-sm">Admin</span>
          <button className="ml-auto lg:hidden" onClick={() => setNavOpen(false)}><X className="w-5 h-5" /></button>
        </div>
        <nav className="flex-1 overflow-y-auto py-3">
          {NAV.map((n) => (
            <button key={n.key} data-testid={`nav-${n.key}`} onClick={() => { setSection(n.key); setNavOpen(false); }}
              className={`w-full flex items-center gap-3 px-5 py-3 text-sm uppercase tracking-wide transition-colors ${section === n.key ? "bg-primary/10 text-primary border-l-2 border-primary" : "text-muted-foreground hover:text-white"}`}>
              <n.icon className="w-4 h-4" /> {n.label}
            </button>
          ))}
        </nav>
        <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-3 px-5 py-4 text-sm uppercase tracking-wide text-muted-foreground hover:text-destructive border-t border-white/10">
          <LogOut className="w-4 h-4" /> Log Out
        </button>
      </aside>
      {navOpen && <div className="fixed inset-0 bg-black/60 z-40 lg:hidden" onClick={() => setNavOpen(false)} />}

      {/* Main */}
      <div className="flex-1 min-w-0">
        <div className="h-16 border-b border-white/10 flex items-center gap-3 px-5 sticky top-0 bg-black/80 backdrop-blur-xl z-30">
          <button className="lg:hidden" onClick={() => setNavOpen(true)}><Menu className="w-6 h-6" /></button>
          <h1 className="font-head font-black uppercase tracking-tight text-lg">{NAV.find((n) => n.key === section)?.label}</h1>
          <a href="/" target="_blank" rel="noreferrer" className="ml-auto text-xs uppercase tracking-wide text-muted-foreground hover:text-primary">View Site ↗</a>
        </div>
        <div className="p-5 sm:p-8 max-w-6xl">
          {section === "appointments" && <Appointments />}
          {section === "calendar" && <CalendarView />}
          {section === "customers" && <Customers />}
          {section === "services" && <Services />}
          {section === "films" && <ManageCollection coll="films" title="Film Type"
            schema={[{ key: "name", label: "Name", type: "text" }, { key: "description", label: "Description", type: "textarea" }, { key: "price", label: "Price Adder ($)", type: "number" }, { key: "available", label: "Available", type: "switch" }]}
            columns={[["name", "Name"], ["price", "Price Adder"]]} quickToggle="available" />}
          {section === "services" && null}
          {section === "percentages" && <ManageCollection coll="percentages" title="Tint Percentage"
            schema={[{ key: "label", label: "Label", type: "text" }, { key: "value", label: "Value (number)", type: "number" }, { key: "available", label: "Available", type: "switch" }]}
            columns={[["label", "Label"], ["value", "Value"]]} quickToggle="available" />}
          {section === "vehicle-types" && <ManageCollection coll="vehicle-types" title="Vehicle Type"
            schema={[{ key: "name", label: "Name", type: "text" }, { key: "price_adjustment_amount", label: "$ Added per vehicle (e.g. SUV = 30)", type: "number" }]} columns={[["name", "Name"], ["price_adjustment_amount", "+$"]]} />}
          {section === "promotions" && <ManageCollection coll="promotions" title="Promotion"
            schema={[{ key: "title", label: "Title", type: "text" }, { key: "description", label: "Description", type: "textarea" }, { key: "active", label: "Active", type: "switch" }]}
            columns={[["title", "Title"], ["description", "Description"]]} quickToggle="active" />}
          {section === "gallery" && <ManageCollection coll="gallery" title="Gallery Image"
            schema={[{ key: "caption", label: "Caption", type: "text" }, { key: "type", label: "Type", type: "select", options: ["gallery", "before_after"] }, { key: "image_url", label: "Image URL (or upload below)", type: "text" }]}
            columns={[["caption", "Caption"], ["type", "Type"]]} image="image_url" upload="image_url" />}
          {section === "reviews" && <ManageCollection coll="reviews" title="Review"
            schema={[{ key: "name", label: "Customer Name", type: "text" }, { key: "rating", label: "Rating (1-5)", type: "number" }, { key: "text", label: "Review", type: "textarea" }, { key: "active", label: "Active", type: "switch" }]}
            columns={[["name", "Name"], ["rating", "Rating"]]} quickToggle="active" />}
          {section === "blocked" && <Blocked />}
          {section === "hours" && <Hours />}
          {section === "settings" && <SettingsPanel />}
        </div>
      </div>
    </div>
  );
}

// ---------------- Generic field renderer ----------------
function FieldInput({ def, value, onChange }) {
  if (def.type === "textarea")
    return <Textarea className="rounded-none bg-black border-white/10 mt-1" value={value ?? ""} onChange={(e) => onChange(e.target.value)} />;
  if (def.type === "switch")
    return <div className="mt-2"><Switch checked={!!value} onCheckedChange={onChange} /></div>;
  if (def.type === "select")
    return (
      <Select value={value ?? ""} onValueChange={onChange}>
        <SelectTrigger className="rounded-none bg-black border-white/10 mt-1"><SelectValue /></SelectTrigger>
        <SelectContent>{def.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
  return <Input type={def.type === "number" ? "number" : "text"} className="rounded-none bg-black border-white/10 mt-1"
    value={value ?? ""} onChange={(e) => onChange(def.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)} />;
}

// ---------------- Generic collection manager ----------------
function ManageCollection({ coll, title, schema, columns, quickToggle, image, upload }) {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/collections/${coll}`).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
  }, [coll]);
  useEffect(() => { load(); }, [load]);

  const startNew = () => { const init = {}; schema.forEach((s) => (init[s.key] = s.type === "switch" ? true : s.type === "number" ? 0 : "")); setEditing(init); setOpen(true); };
  const startEdit = (it) => { setEditing({ ...it }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/collections/${coll}/${editing.id}`, editing);
      else await api.post(`/admin/collections/${coll}`, editing);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this item?")) return; try { await api.delete(`/admin/collections/${coll}/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (it) => { try { await api.put(`/admin/collections/${coll}/${it.id}`, { [quickToggle]: !it[quickToggle] }); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <Button data-testid={`add-${coll}`} onClick={startNew} className="rounded-none bg-primary text-primary-foreground font-bold uppercase tracking-wider mb-5"><Plus className="w-4 h-4 mr-2" />Add {title}</Button>
      <div className="border border-white/10 divide-y divide-white/5">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">No items yet.</p>}
        {items.map((it) => (
          <div key={it.id} data-testid={`row-${coll}-${it.id}`} className="flex items-center gap-3 p-4">
            {image && <img src={imgUrl(it[image])} alt="" className="w-14 h-14 object-cover border border-white/10 shrink-0" />}
            <div className="min-w-0 flex-1">
              {columns.map(([k, lbl]) => (
                <p key={k} className="text-sm truncate"><span className="text-muted-foreground uppercase text-xs tracking-wide">{lbl}: </span>{String(it[k])}</p>
              ))}
            </div>
            {quickToggle && (
              <div className="flex items-center gap-2 shrink-0">
                <span className={`text-xs uppercase ${it[quickToggle] ? "text-primary" : "text-destructive"}`}>{it[quickToggle] ? "On" : "Off"}</span>
                <Switch data-testid={`toggle-${coll}-${it.id}`} checked={!!it[quickToggle]} onCheckedChange={() => toggle(it)} />
              </div>
            )}
            <button data-testid={`edit-${coll}-${it.id}`} onClick={() => startEdit(it)} className="p-2 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
            <button data-testid={`delete-${coll}-${it.id}`} onClick={() => remove(it.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#0b0b0b] border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head uppercase">{editing?.id ? "Edit" : "Add"} {title}</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              {schema.map((def) => (
                <div key={def.key}>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">{def.label}</Label>
                  <FieldInput def={def} value={editing[def.key]} onChange={(v) => setEditing((e) => ({ ...e, [def.key]: v }))} />
                </div>
              ))}
              {upload && (
                <div>
                  <Label className="text-xs uppercase tracking-wide text-muted-foreground">Or Upload Image</Label>
                  <ImageUploader value={editing[upload]} onChange={(url) => setEditing((e) => ({ ...e, [upload]: url }))} />
                </div>
              )}
            </div>
          )}
          <DialogFooter><Button data-testid="save-item" onClick={save} className="rounded-none bg-primary text-primary-foreground font-bold uppercase">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Services (custom: prices + allowed percentages) ----------------
function Services() {
  const [items, setItems] = useState([]);
  const [vtypes, setVtypes] = useState([]);
  const [pcts, setPcts] = useState([]);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);

  const load = useCallback(() => {
    Promise.all([
      api.get("/admin/collections/services"),
      api.get("/admin/collections/vehicle-types"),
      api.get("/admin/collections/percentages"),
    ]).then(([s, v, p]) => { setItems(s.data); setVtypes(v.data); setPcts(p.data); }).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const startNew = () => setEditing({ name: "", description: "", image_url: "", available: true, base_price: 0, price_display: "", allowed_percentages: pcts.map((p) => p.id) }) || setOpen(true);
  const startEdit = (it) => { setEditing({ ...it, prices: { ...(it.prices || {}) }, allowed_percentages: [...(it.allowed_percentages || [])] }); setOpen(true); };

  const save = async () => {
    try {
      if (editing.id) await api.put(`/admin/collections/services/${editing.id}`, editing);
      else await api.post("/admin/collections/services", editing);
      toast.success("Saved"); setOpen(false); setEditing(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this service?")) return; try { await api.delete(`/admin/collections/services/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  const toggle = async (it) => { try { await api.put(`/admin/collections/services/${it.id}`, { available: !it.available }); load(); } catch (e) { toast.error(apiError(e)); } };

  return (
    <div>
      <Button data-testid="add-service" onClick={startNew} className="rounded-none bg-primary text-primary-foreground font-bold uppercase tracking-wider mb-5"><Plus className="w-4 h-4 mr-2" />Add Service</Button>
      <div className="grid gap-4">
        {items.map((s) => (
          <div key={s.id} data-testid={`admin-service-${s.id}`} className="border border-white/10 bg-[#0b0b0b] p-4 flex gap-4 items-start">
            <img src={imgUrl(s.image_url)} alt="" className="w-20 h-20 object-cover border border-white/10 shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="font-head font-bold uppercase">{s.name}</h3>
                {s.available ? <Badge className="rounded-none bg-primary text-primary-foreground text-[10px]">On</Badge> : <Badge className="rounded-none bg-destructive text-white text-[10px]">Off</Badge>}
              </div>
              <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{s.description}</p>
              <p className="text-sm mt-2 text-primary">{vtypes.map((v) => `${v.name}: ${money(Math.round(((s.base_price || 0) + (v.price_adjustment_amount || 0)) * 100) / 100)}`).join("  •  ")}</p>
            </div>
            <div className="flex flex-col items-end gap-2 shrink-0">
              <Switch data-testid={`toggle-service-${s.id}`} checked={!!s.available} onCheckedChange={() => toggle(s)} />
              <div className="flex gap-1">
                <button data-testid={`edit-service-${s.id}`} onClick={() => startEdit(s)} className="p-2 text-muted-foreground hover:text-primary"><Pencil className="w-4 h-4" /></button>
                <button data-testid={`delete-service-${s.id}`} onClick={() => remove(s.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none bg-[#0b0b0b] border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head uppercase">{editing?.id ? "Edit" : "Add"} Service</DialogTitle></DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Name</Label>
                <Input className="rounded-none bg-black border-white/10 mt-1" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Description</Label>
                <Textarea className="rounded-none bg-black border-white/10 mt-1" value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Image URL</Label>
                <Input className="rounded-none bg-black border-white/10 mt-1" value={editing.image_url} onChange={(e) => setEditing({ ...editing, image_url: e.target.value })} /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Or Upload Image</Label>
                <ImageUploader value={editing.image_url} onChange={(url) => setEditing({ ...editing, image_url: url })} /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Duration (shown to customers)</Label>
                <Input className="rounded-none bg-black border-white/10 mt-1" value={editing.duration ?? ""} onChange={(e) => setEditing({ ...editing, duration: e.target.value })} placeholder="Approx. 2 hours" /></div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Duration Minutes (reserves this much time)</Label>
                <Input type="number" data-testid="service-duration-min" className="rounded-none bg-black border-white/10 mt-1 w-40" value={editing.duration_minutes ?? 60} onChange={(e) => setEditing({ ...editing, duration_minutes: Number(e.target.value) })} /></div>
              <div className="flex items-center gap-6 flex-wrap">
                <label className="flex items-center gap-2"><Switch checked={!!editing.requires_film} onCheckedChange={(v) => setEditing({ ...editing, requires_film: v })} /><span className="text-xs uppercase text-muted-foreground">Requires Film</span></label>
                <label className="flex items-center gap-2"><Switch checked={!!editing.requires_percentage} onCheckedChange={(v) => setEditing({ ...editing, requires_percentage: v })} /><span className="text-xs uppercase text-muted-foreground">Requires Tint %</span></label>
                <label className="flex items-center gap-2"><Switch checked={!!editing.duration_estimated} onCheckedChange={(v) => setEditing({ ...editing, duration_estimated: v })} /><span className="text-xs uppercase text-muted-foreground">Duration is Estimate</span></label>
                <label className="flex items-center gap-2"><Switch data-testid="service-online-bookable" checked={editing.online_bookable !== false} onCheckedChange={(v) => setEditing({ ...editing, online_bookable: v })} /><span className="text-xs uppercase text-muted-foreground">Bookable Online (off = call only)</span></label>
              </div>
              <div className="flex items-center gap-3"><Label className="text-xs uppercase tracking-wide text-muted-foreground">Available</Label>
                <Switch checked={!!editing.available} onCheckedChange={(v) => setEditing({ ...editing, available: v })} /></div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Base Price ($) — standard vehicle</Label>
                <Input type="number" data-testid="service-base-price" className="rounded-none bg-black border-white/10 mt-1 w-40" value={editing.base_price ?? 0}
                  onChange={(e) => setEditing({ ...editing, base_price: Number(e.target.value) })} />
                <div className="grid grid-cols-2 gap-2 mt-3">
                  {vtypes.map((v) => (
                    <div key={v.id} className="border border-white/10 p-2">
                      <span className="text-xs text-muted-foreground">{v.name}{v.price_adjustment_amount ? ` (+${money(v.price_adjustment_amount)})` : ""}</span>
                      <p className="text-primary font-semibold">{money(Math.round(((editing.base_price || 0) + (v.price_adjustment_amount || 0)) * 100) / 100)}</p>
                    </div>
                  ))}
                </div>
                <p className="text-[11px] text-muted-foreground mt-2">Vehicle price adjustments (e.g. SUV +$30) are set in the Vehicle Types panel.</p>
              </div>
              <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">Price Display Override (optional)</Label>
                <Input data-testid="service-price-display" className="rounded-none bg-black border-white/10 mt-1" value={editing.price_display ?? ""} onChange={(e) => setEditing({ ...editing, price_display: e.target.value })} placeholder="e.g. $150–$250 (blank shows base price)" /></div>
              <div>
                <Label className="text-xs uppercase tracking-wide text-muted-foreground">Allowed Tint Percentages</Label>
                <div className="grid grid-cols-3 gap-2 mt-2">
                  {pcts.map((p) => {
                    const on = (editing.allowed_percentages || []).includes(p.id);
                    return (
                      <label key={p.id} className="flex items-center gap-2 border border-white/10 p-2 cursor-pointer">
                        <Checkbox checked={on} onCheckedChange={(c) => {
                          const list = new Set(editing.allowed_percentages || []);
                          c ? list.add(p.id) : list.delete(p.id);
                          setEditing({ ...editing, allowed_percentages: [...list] });
                        }} className="rounded-none border-white/30" />
                        <span className="text-sm">{p.label}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-service" onClick={save} className="rounded-none bg-primary text-primary-foreground font-bold uppercase">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Appointments ----------------
const STATUSES = ["pending", "confirmed", "completed", "cancelled", "no_show"];
function Appointments() {
  const [items, setItems] = useState([]);
  const [stats, setStats] = useState({});
  const [filter, setFilter] = useState("");
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [refs, setRefs] = useState({ services: [], vehicle_types: [], percentages: [] });

  const load = useCallback(() => {
    api.get(`/admin/appointments/list${filter ? `?status=${filter}` : ""}`).then((r) => setItems(r.data)).catch((e) => toast.error(apiError(e)));
    api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
  }, [filter]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    Promise.all([api.get("/admin/collections/services"), api.get("/admin/collections/vehicle-types"), api.get("/admin/collections/percentages")])
      .then(([s, v, p]) => setRefs({ services: s.data, vehicle_types: v.data, percentages: p.data })).catch(() => {});
  }, []);

  const setStatus = async (it, status) => { try { await api.put(`/admin/appointments/${it.id}`, { status }); load(); } catch (e) { toast.error(apiError(e)); } };
  const remove = async (id) => { if (!window.confirm("Delete this appointment?")) return; try { await api.delete(`/admin/appointments/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };

  const startNew = () => { setEditing({ customer_name: "", phone: "", email: "", service_id: "", service_name: "", vehicle_type_id: "", vehicle_type_name: "", percentage_id: "", percentage_label: "", vehicle_year: "", vehicle_make: "", vehicle_model: "", location: "shop", date: "", time: "", price: 0, total: 0, status: "confirmed", notes: "" }); setOpen(true); };
  const startEdit = (it) => { setEditing({ ...it }); setOpen(true); };

  const save = async () => {
    const payload = { ...editing };
    const svc = refs.services.find((s) => s.id === payload.service_id); if (svc) payload.service_name = svc.name;
    const vt = refs.vehicle_types.find((v) => v.id === payload.vehicle_type_id); if (vt) payload.vehicle_type_name = vt.name;
    const pc = refs.percentages.find((p) => p.id === payload.percentage_id); if (pc) payload.percentage_label = pc.label;
    payload.total = Number(payload.total || payload.price || 0);
    try {
      if (payload.id) await api.put(`/admin/appointments/${payload.id}`, payload);
      else await api.post("/admin/appointments/create", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const statusColor = (s) => s === "confirmed" ? "bg-blue-100 text-blue-700" : s === "completed" ? "bg-green-100 text-green-700" : s === "cancelled" ? "bg-red-100 text-red-700" : s === "no_show" ? "bg-orange-100 text-orange-700" : "bg-slate-200 text-slate-700";
  const INP = "rounded-lg bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 mt-1 h-11";
  const LBL = "block text-xs font-semibold uppercase tracking-wide text-slate-500";
  const modelOpts = (() => {
    const mk = CAR_MAKES.find((m) => m.toLowerCase() === (editing?.vehicle_make || "").trim().toLowerCase());
    return mk ? (MODELS_BY_MAKE[mk] || []) : [];
  })();

  return (
    <div className="bg-slate-50 text-slate-900 -mx-5 sm:-mx-8 -mt-5 sm:-mt-8 px-4 sm:px-8 py-6 min-h-[calc(100vh-4rem)]">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
        {[["Total", stats.total], ["Pending", stats.pending], ["Today", stats.today], ["Upcoming", stats.upcoming]].map(([k, v]) => (
          <div key={k} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <p className="text-xs uppercase tracking-wide text-slate-500">{k}</p>
            <p className="font-head font-black text-3xl text-blue-600 mt-1">{v ?? 0}</p>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <Button data-testid="add-appointment" onClick={startNew} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-5"><Plus className="w-4 h-4 mr-2" />Add Appointment</Button>
        <Select value={filter || "all"} onValueChange={(v) => setFilter(v === "all" ? "" : v)}>
          <SelectTrigger data-testid="filter-status" className="rounded-lg bg-white border-slate-300 text-slate-900 w-44 h-11"><SelectValue placeholder="All statuses" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All statuses</SelectItem>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="space-y-3">
        {items.length === 0 && <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-slate-500">No appointments yet.</div>}
        {items.map((a) => (
          <div key={a.id} data-testid={`appt-${a.id}`} className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-bold text-lg text-slate-900">{a.customer_name}</span>
                  <Badge className={`rounded-full text-[11px] px-2.5 py-0.5 font-semibold ${statusColor(a.status)}`}>{a.status}</Badge>
                  <Badge data-testid={`appt-source-${a.id}`} className={`rounded-full text-[11px] px-2.5 py-0.5 font-semibold ${a.source === "online" ? "bg-emerald-100 text-emerald-700" : "bg-slate-200 text-slate-700"}`}>{a.source === "online" ? "Booked Online" : "Added by You"}</Badge>
                </div>
                <p className="text-sm text-slate-600 mt-1">{[a.service_name, a.percentage_label, a.vehicle_type_name].filter(Boolean).join(" · ")}</p>
              </div>
              <div className="text-right shrink-0">
                <p className="font-head font-black text-xl text-blue-600">{money(a.total)}</p>
                <p className="text-xs text-slate-500">{a.location === "mobile" ? "Mobile" : "Shop"}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 mt-3 text-sm">
              <div className="flex items-center gap-2 text-slate-700"><Calendar className="w-4 h-4 text-slate-400 shrink-0" />{a.date || "—"} · {a.time || "—"}</div>
              <div className="flex items-center gap-2 text-slate-700"><Car className="w-4 h-4 text-slate-400 shrink-0" />{`${a.vehicle_year || ""} ${a.vehicle_make || ""} ${a.vehicle_model || ""}`.trim() || "—"}</div>
              <div className="flex items-center gap-2 text-slate-900 font-semibold"><Phone className="w-4 h-4 text-slate-400 shrink-0" />{a.phone || "—"}</div>
              {a.email && <div className="flex items-center gap-2 text-slate-700 truncate"><span className="text-slate-400">@</span><span className="truncate">{a.email}</span></div>}
            </div>
            {a.notes && <p className="text-xs text-slate-500 mt-2 italic">“{a.notes}”</p>}
            <div className="flex items-center gap-2 mt-4 pt-3 border-t border-slate-100 flex-wrap">
              {a.phone && (
                <>
                  <a href={telHref(a.phone)} data-testid={`call-appt-${a.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-green-50 text-green-700 hover:bg-green-100 text-sm font-semibold transition-colors"><Phone className="w-4 h-4" />Call</a>
                  <a href={smsHref(a.phone)} data-testid={`text-appt-${a.id}`} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 hover:bg-blue-100 text-sm font-semibold transition-colors"><MessageSquare className="w-4 h-4" />Text</a>
                </>
              )}
              <Select value={a.status} onValueChange={(v) => setStatus(a, v)}>
                <SelectTrigger data-testid={`status-${a.id}`} className="rounded-lg bg-white border-slate-300 text-slate-900 w-36 h-10 text-sm"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
              <button data-testid={`edit-appt-${a.id}`} onClick={() => startEdit(a)} className="ml-auto inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-100 text-slate-700 hover:bg-slate-200 text-sm font-semibold transition-colors"><Pencil className="w-4 h-4" />Edit</button>
              <button data-testid={`delete-appt-${a.id}`} onClick={() => remove(a.id)} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-50 text-red-600 hover:bg-red-100 text-sm font-semibold transition-colors"><Trash2 className="w-4 h-4" />Delete</button>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-xl bg-white text-slate-900 border-slate-200 max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head text-xl text-slate-900">{editing?.id ? "Edit" : "Add"} Appointment</DialogTitle>
            <DialogDescription className="text-slate-500 text-sm">Fill in the customer and appointment details below.</DialogDescription></DialogHeader>
          {editing && (
            <div className="space-y-3">
              <div><label className={LBL}>Customer Name</label>
                <Input data-testid="appt-field-name" className={INP} value={editing.customer_name ?? ""} onChange={(e) => setEditing({ ...editing, customer_name: e.target.value })} placeholder="John Doe" /></div>
              <div><label className={LBL}>Phone</label>
                <Input data-testid="appt-field-phone" type="text" inputMode="tel" autoComplete="off" className={INP} value={editing.phone ?? ""} onChange={(e) => setEditing({ ...editing, phone: e.target.value })} placeholder="(559) 555-1234" /></div>
              <div><label className={LBL}>Email</label>
                <Input data-testid="appt-field-email" type="text" inputMode="email" autoComplete="off" className={INP} value={editing.email ?? ""} onChange={(e) => setEditing({ ...editing, email: e.target.value })} placeholder="you@email.com" /></div>
              <div className="grid grid-cols-3 gap-2">
                <div><label className={LBL}>Vehicle Year</label>
                  <AutoField testid="appt-field-year" value={editing.vehicle_year ?? ""} onChange={(v) => setEditing({ ...editing, vehicle_year: v })} options={YEARS} placeholder="2022" /></div>
                <div><label className={LBL}>Vehicle Make</label>
                  <AutoField testid="appt-field-make" value={editing.vehicle_make ?? ""} onChange={(v) => setEditing({ ...editing, vehicle_make: v, vehicle_model: "" })} options={CAR_MAKES} placeholder="Toyota" /></div>
                <div><label className={LBL}>Vehicle Model</label>
                  <AutoField testid="appt-field-model" value={editing.vehicle_model ?? ""} onChange={(v) => setEditing({ ...editing, vehicle_model: v })} options={modelOpts} placeholder="Camry" /></div>
              </div>
              <div><label className={LBL}>Service</label>
                <Select value={editing.service_id || ""} onValueChange={(v) => setEditing({ ...editing, service_id: v })}>
                  <SelectTrigger className={INP}><SelectValue placeholder="Select service" /></SelectTrigger>
                  <SelectContent>{refs.services.filter((s) => s.available).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className={LBL}>Vehicle Type</label>
                <Select value={editing.vehicle_type_id || ""} onValueChange={(v) => setEditing({ ...editing, vehicle_type_id: v })}>
                  <SelectTrigger className={INP}><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{refs.vehicle_types.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className={LBL}>Date</label>
                  <Input data-testid="appt-field-date" type="date" className={INP} value={editing.date ?? ""} onChange={(e) => setEditing({ ...editing, date: e.target.value })} /></div>
                <div><label className={LBL}>Time</label>
                  <Input data-testid="appt-field-time" type="time" className={INP} value={editing.time ?? ""} onChange={(e) => setEditing({ ...editing, time: e.target.value })} /></div>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div><label className={LBL}>Location</label>
                  <Select value={editing.location || "shop"} onValueChange={(v) => setEditing({ ...editing, location: v })}>
                    <SelectTrigger className={INP}><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="shop">Shop</SelectItem><SelectItem value="mobile">Mobile</SelectItem></SelectContent>
                  </Select></div>
                <div><label className={LBL}>Total Price ($)</label>
                  <Input data-testid="appt-field-total" type="number" className={INP} value={editing.total ?? 0} onChange={(e) => setEditing({ ...editing, total: Number(e.target.value), price: Number(e.target.value) })} /></div>
              </div>
              <div><label className={LBL}>Notes</label>
                <Textarea data-testid="appt-field-notes" className="rounded-lg bg-white text-slate-900 border-slate-300 placeholder:text-slate-400 mt-1" value={editing.notes ?? ""} onChange={(e) => setEditing({ ...editing, notes: e.target.value })} placeholder="Anything we should know?" /></div>
            </div>
          )}
          <DialogFooter><Button data-testid="save-appt" onClick={save} className="rounded-lg bg-blue-600 hover:bg-blue-700 text-white font-bold h-11 px-6 w-full sm:w-auto">Save Appointment</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ---------------- Availability / Blocked ----------------
function Blocked() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ date: "", time: "", type: "block", message: "", reason: "" });
  const load = useCallback(() => api.get("/admin/blocked/list").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    if (!form.date) return toast.error("Date required");
    const payload = { date: form.date, reason: form.reason, message: form.message };
    if (form.type === "block") { payload.time = form.time || null; payload.type = form.time ? "block" : "closed"; }
    else { payload.time = null; payload.type = form.type; }
    try { await api.post("/admin/blocked/create", payload); setForm({ date: "", time: "", type: "block", message: "", reason: "" }); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const remove = async (id) => { try { await api.delete(`/admin/blocked/${id}`); load(); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <p className="text-sm text-muted-foreground mb-4">Mark a date Closed or Fully Booked, block a whole day, or block a specific time slot. To disable ALL online booking, use the toggle in Settings.</p>
      <div className="grid sm:grid-cols-2 gap-3 items-end mb-6 border border-white/10 p-4 bg-[#0b0b0b]">
        <div><Label className="text-xs uppercase text-muted-foreground">Date</Label><Input data-testid="block-date" className="rounded-none bg-black border-white/10 mt-1" placeholder="2026-06-20" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Type</Label>
          <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
            <SelectTrigger data-testid="block-type" className="rounded-none bg-black border-white/10 mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="block">Block a time (or whole day)</SelectItem>
              <SelectItem value="closed">Mark date Closed</SelectItem>
              <SelectItem value="fully_booked">Mark date Fully Booked</SelectItem>
            </SelectContent>
          </Select></div>
        {form.type === "block" && <div><Label className="text-xs uppercase text-muted-foreground">Time (blank = whole day)</Label><Input data-testid="block-time" className="rounded-none bg-black border-white/10 mt-1" placeholder="14:00" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} /></div>}
        <div><Label className="text-xs uppercase text-muted-foreground">Custom Message (optional)</Label><Input className="rounded-none bg-black border-white/10 mt-1" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Closed for holiday" /></div>
        <Button data-testid="add-block" onClick={add} className="rounded-none bg-primary text-primary-foreground font-bold uppercase self-end"><Plus className="w-4 h-4 mr-2" />Add</Button>
      </div>
      <div className="border border-white/10 divide-y divide-white/5">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">Nothing blocked.</p>}
        {items.map((b) => (
          <div key={b.id} className="p-4 flex items-center justify-between">
            <span className="text-sm">{b.date}{b.time ? ` @ ${b.time}` : ""} · <span className="uppercase text-primary text-xs tracking-wide">{(b.type || "block").replace("_", " ")}</span>{b.message ? ` · ${b.message}` : ""}</span>
            <button data-testid={`del-block-${b.id}`} onClick={() => remove(b.id)} className="p-2 text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}

function ImageUploader({ value, onChange }) {
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", f);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url); toast.success("Image uploaded");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <div className="mt-2">
      {value && <img src={imgUrl(value)} alt="" className="w-24 h-24 object-cover border border-white/10 mb-2" />}
      <label className="inline-flex items-center gap-2 border border-white/20 px-3 py-2 cursor-pointer text-xs uppercase tracking-wide hover:border-primary">
        <UploadCloud className="w-4 h-4" /> {busy ? "Uploading…" : "Choose Image"}
        <input type="file" accept="image/*" onChange={onFile} data-testid="image-upload-input" className="hidden" />
      </label>
    </div>
  );
}

function Customers() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [history, setHistory] = useState(null);
  const load = useCallback(() => api.get(`/admin/customers${search ? `?search=${encodeURIComponent(search)}` : ""}`).then((r) => setItems(r.data)).catch(() => {}), [search]);
  useEffect(() => { load(); }, [load]);
  const viewHistory = async (c) => { try { const { data } = await api.get(`/admin/customers/history?phone=${encodeURIComponent(c.phone)}`); setHistory({ customer: c, appts: data }); } catch (e) { toast.error(apiError(e)); } };
  return (
    <div>
      <Input data-testid="customer-search" className="rounded-none bg-black border-white/10 mb-5 max-w-sm" placeholder="Search name, phone or email…" value={search} onChange={(e) => setSearch(e.target.value)} />
      <div className="border border-white/10 divide-y divide-white/5">
        {items.length === 0 && <p className="p-5 text-sm text-muted-foreground">No customers yet.</p>}
        {items.map((c) => (
          <div key={c.key} data-testid={`customer-${c.key}`} className="p-4 flex items-center justify-between gap-3">
            <div className="min-w-0">
              <p className="font-head font-bold uppercase">{c.name}</p>
              <p className="text-xs text-muted-foreground">{c.phone}{c.email ? ` · ${c.email}` : ""}</p>
              <p className="text-xs text-muted-foreground mt-1">{c.count} appointment{c.count !== 1 ? "s" : ""} · last {c.last_date} ({c.last_service})</p>
            </div>
            <Button data-testid={`history-${c.key}`} variant="outline" onClick={() => viewHistory(c)} className="rounded-none border-white/20 bg-transparent text-xs uppercase shrink-0">History</Button>
          </div>
        ))}
      </div>
      <Dialog open={!!history} onOpenChange={(o) => !o && setHistory(null)}>
        <DialogContent className="rounded-none bg-[#0b0b0b] border-white/10 max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head uppercase">{history?.customer?.name} — History</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {history?.appts?.map((a) => (
              <div key={a.id} className="border border-white/10 p-3 text-sm">
                <p className="font-semibold">{a.date} {a.time} · <span className="uppercase text-xs text-primary">{a.status}</span></p>
                <p className="text-xs text-muted-foreground">{a.service_name}{a.film_name ? ` · ${a.film_name}` : ""}{a.percentage_label ? ` · ${a.percentage_label}` : ""} · {money(a.total)}</p>
              </div>
            ))}
            {history?.appts?.length === 0 && <p className="text-sm text-muted-foreground">No appointments.</p>}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CalendarView() {
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(() => new Date());
  const [appts, setAppts] = useState([]);
  useEffect(() => { api.get("/admin/appointments/list").then((r) => setAppts(r.data)).catch(() => {}); }, []);
  const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const days = view === "day" ? [new Date(anchor)] : (() => {
    const start = new Date(anchor); start.setDate(start.getDate() - start.getDay());
    return Array.from({ length: 7 }).map((_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  })();
  const shift = (n) => { const d = new Date(anchor); d.setDate(d.getDate() + n * (view === "day" ? 1 : 7)); setAnchor(d); };
  const forDay = (d) => appts.filter((a) => a.date === iso(d) && a.status !== "cancelled").sort((x, y) => (x.time || "").localeCompare(y.time || ""));
  return (
    <div>
      <div className="flex items-center gap-3 mb-5 flex-wrap">
        <div className="flex border border-white/10">
          <button data-testid="cal-day" onClick={() => setView("day")} className={`px-4 py-2 text-xs uppercase ${view === "day" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Day</button>
          <button data-testid="cal-week" onClick={() => setView("week")} className={`px-4 py-2 text-xs uppercase ${view === "week" ? "bg-primary text-primary-foreground" : "text-muted-foreground"}`}>Week</button>
        </div>
        <Button variant="outline" onClick={() => shift(-1)} className="rounded-none border-white/20 bg-transparent">‹ Prev</Button>
        <Button variant="outline" onClick={() => setAnchor(new Date())} className="rounded-none border-white/20 bg-transparent text-xs uppercase">Today</Button>
        <Button variant="outline" onClick={() => shift(1)} className="rounded-none border-white/20 bg-transparent">Next ›</Button>
      </div>
      <div className={`grid gap-3 ${view === "week" ? "grid-cols-1 md:grid-cols-7" : "grid-cols-1"}`}>
        {days.map((d) => (
          <div key={iso(d)} className="border border-white/10 bg-[#0b0b0b] min-h-[120px]">
            <div className="px-3 py-2 border-b border-white/10 text-xs uppercase tracking-wide text-primary font-bold">{d.toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" })}</div>
            <div className="p-2 space-y-1">
              {forDay(d).length === 0 && <p className="text-xs text-muted-foreground p-1">—</p>}
              {forDay(d).map((a) => (
                <div key={a.id} className="border border-white/10 p-2 text-xs">
                  <p className="font-semibold text-primary">{a.time}</p>
                  <p className="truncate">{a.customer_name}</p>
                  <p className="text-muted-foreground truncate">{a.service_name}</p>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------- Business hours ----------------
const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
function Hours() {
  const [settings, setSettings] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setSettings(r.data)); }, []);
  const save = async () => {
    try {
      await api.put("/admin/settings", {
        hours: settings.hours,
        slot_interval_minutes: settings.slot_interval_minutes,
        windshield_slot_interval_minutes: settings.windshield_slot_interval_minutes,
        online_booking_enabled: settings.online_booking_enabled,
        online_booking_message: settings.online_booking_message,
      });
      toast.success("Saved");
    } catch (e) { toast.error(apiError(e)); }
  };
  if (!settings) return null;
  const upd = (day, k, v) => setSettings({ ...settings, hours: { ...settings.hours, [day]: { ...settings.hours[day], [k]: v } } });
  const open24 = (day) => setSettings({ ...settings, hours: { ...settings.hours, [day]: { open: "00:00", close: "23:59", closed: false } } });
  const bookingOn = settings.online_booking_enabled !== false;
  return (
    <div className="max-w-2xl">
      <div className="border border-primary/30 bg-primary/5 p-4 mb-6 text-sm text-muted-foreground">
        <p className="text-primary font-head font-bold uppercase text-xs tracking-wide mb-1">Your website is open 24/7</p>
        Customers can Call, Text, and Book online any time — day or night. The business hours below only control which appointment time slots customers can choose, not whether the site is available.
      </div>

      <div className="border border-white/10 p-4 mb-6 space-y-3">
        <div className="flex items-center gap-3">
          <Label className="text-xs uppercase text-muted-foreground">Accept Online Bookings</Label>
          <Switch data-testid="hours-online-booking" checked={bookingOn} onCheckedChange={(v) => setSettings({ ...settings, online_booking_enabled: v })} />
          <span className={`text-xs uppercase ${bookingOn ? "text-primary" : "text-destructive"}`}>{bookingOn ? "On — booking open 24/7" : "Off — call/text only"}</span>
        </div>
        {!bookingOn && (
          <div><Label className="text-xs uppercase text-muted-foreground">Message shown when booking is off</Label>
            <Textarea className="rounded-none bg-black border-white/10 mt-1" value={settings.online_booking_message ?? ""} onChange={(e) => setSettings({ ...settings, online_booking_message: e.target.value })} /></div>
        )}
      </div>

      <div className="grid grid-cols-2 gap-4 mb-5">
        <div><Label className="text-xs uppercase text-muted-foreground">Regular Slot Interval (min)</Label>
          <Input type="number" data-testid="slot-interval" className="rounded-none bg-black border-white/10 mt-1" value={settings.slot_interval_minutes} onChange={(e) => setSettings({ ...settings, slot_interval_minutes: Number(e.target.value) })} /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Windshield Slot Interval (min)</Label>
          <Input type="number" data-testid="windshield-interval" className="rounded-none bg-black border-white/10 mt-1" value={settings.windshield_slot_interval_minutes ?? 120} onChange={(e) => setSettings({ ...settings, windshield_slot_interval_minutes: Number(e.target.value) })} /></div>
      </div>
      <p className="text-xs uppercase tracking-wide text-muted-foreground mb-2">Business Hours (appointment slot window)</p>
      <div className="border border-white/10 divide-y divide-white/5">
        {DAYS.map((d) => (
          <div key={d} className="p-4 flex items-center gap-3 flex-wrap">
            <span className="w-24 uppercase text-sm font-head font-bold">{d}</span>
            <Switch data-testid={`day-open-${d}`} checked={!settings.hours[d]?.closed} onCheckedChange={(v) => upd(d, "closed", !v)} />
            <span className="text-xs text-muted-foreground w-14">{settings.hours[d]?.closed ? "Closed" : "Open"}</span>
            {!settings.hours[d]?.closed && (
              <>
                <Input className="rounded-none bg-black border-white/10 w-28" value={settings.hours[d]?.open} onChange={(e) => upd(d, "open", e.target.value)} />
                <span className="text-muted-foreground">to</span>
                <Input className="rounded-none bg-black border-white/10 w-28" value={settings.hours[d]?.close} onChange={(e) => upd(d, "close", e.target.value)} />
                <button data-testid={`day-24h-${d}`} onClick={() => open24(d)} className="text-[10px] uppercase tracking-wide border border-white/20 px-2 py-1 hover:border-primary text-muted-foreground">Open 24h</button>
              </>
            )}
          </div>
        ))}
      </div>
      <Button data-testid="save-hours" onClick={save} className="rounded-none bg-primary text-primary-foreground font-bold uppercase mt-5">Save</Button>
    </div>
  );
}

// ---------------- Settings ----------------
function SettingsPanel() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/settings").then((r) => setS(r.data)); }, []);
  const save = async () => { try { await api.put("/admin/settings", s); toast.success("Settings saved"); } catch (e) { toast.error(apiError(e)); } };
  if (!s) return null;
  const T = ({ k, label, area }) => (
    <div><Label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</Label>
      {area ? <Textarea className="rounded-none bg-black border-white/10 mt-1" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} />
        : <Input className="rounded-none bg-black border-white/10 mt-1" value={s[k] ?? ""} onChange={(e) => setS({ ...s, [k]: e.target.value })} />}</div>
  );
  return (
    <div className="max-w-2xl space-y-6">
      <section className="space-y-4">
        <h3 className="font-head font-bold uppercase text-primary">Business Info</h3>
        <T k="business_name" label="Business Name" />
        <T k="tagline" label="Tagline" />
        <T k="about" label="About" area />
        <div className="grid sm:grid-cols-2 gap-4"><T k="phone" label="Phone" /><T k="sms_number" label="SMS Number" /></div>
        <div className="grid sm:grid-cols-2 gap-4"><T k="email" label="Email" /><T k="address" label="Address" /></div>
        <T k="hero_image" label="Hero Image URL" />
        <T k="maps_embed_url" label="Google Maps Embed URL" />
        <T k="google_maps_url" label="Google Maps / Directions URL" />
        <T k="google_review_url" label="Google Review URL" />
        <div className="grid sm:grid-cols-2 gap-4"><T k="google_rating" label="Google Rating (e.g. 4.9)" /><T k="google_review_count" label="Google Review Count" /></div>
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Business & Social Links</h3>
        <p className="text-xs text-muted-foreground">Paste full URLs (or @handles). Only filled-in links appear on your site footer.</p>
        {[["google", "Google Business / Maps"], ["instagram", "Instagram"], ["tiktok", "TikTok"], ["facebook", "Facebook"]].map(([k, l]) => (
          <div key={k}><Label className="text-xs uppercase tracking-wide text-muted-foreground">{l}</Label>
            <Input data-testid={`social-${k}`} className="rounded-none bg-black border-white/10 mt-1" value={s.social_links?.[k] ?? ""} onChange={(e) => setS({ ...s, social_links: { ...s.social_links, [k]: e.target.value } })} /></div>
        ))}
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Fees & Payment</h3>
        <div><Label className="text-xs uppercase text-muted-foreground">Mobile Service Fee ($)</Label>
          <Input type="number" data-testid="mobile-fee" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.mobile_fee} onChange={(e) => setS({ ...s, mobile_fee: Number(e.target.value) })} /></div>
        <div className="border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Old Tint Removal Enabled</Label>
            <Switch checked={!!s.old_tint_removal?.enabled} onCheckedChange={(v) => setS({ ...s, old_tint_removal: { ...s.old_tint_removal, enabled: v } })} /></div>
          <div><Label className="text-xs uppercase text-muted-foreground">Removal Price ($)</Label>
            <Input type="number" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.old_tint_removal?.price ?? 0} onChange={(e) => setS({ ...s, old_tint_removal: { ...s.old_tint_removal, price: Number(e.target.value) } })} /></div>
        </div>
        <div><Label className="text-xs uppercase text-muted-foreground">Cash Note</Label>
          <Input className="rounded-none bg-black border-white/10 mt-1" value={s.payment_info?.cash_note ?? ""} onChange={(e) => setS({ ...s, payment_info: { ...s.payment_info, cash_note: e.target.value } })} /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Card Note</Label>
          <Input className="rounded-none bg-black border-white/10 mt-1" value={s.payment_info?.card_note ?? ""} onChange={(e) => setS({ ...s, payment_info: { ...s.payment_info, card_note: e.target.value } })} /></div>
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Notifications</h3>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">1-Hour Reminders (customer)</Label>
          <Switch data-testid="toggle-reminders" checked={!!s.reminders_enabled} onCheckedChange={(v) => setS({ ...s, reminders_enabled: v })} /></div>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">New Booking Alerts (to me)</Label>
          <Switch data-testid="toggle-admin-notif" checked={!!s.admin_notifications_enabled} onCheckedChange={(v) => setS({ ...s, admin_notifications_enabled: v })} /></div>
        <div className="grid sm:grid-cols-2 gap-4"><T k="owner_email" label="My Notification Email" /><T k="owner_phone" label="My Notification Phone (SMS)" /></div>
        <p className="text-xs text-muted-foreground">SMS requires Twilio credentials to be configured. Email works out of the box.</p>
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Online Booking</h3>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Accept Online Bookings</Label>
          <Switch data-testid="toggle-online-booking" checked={!!s.online_booking_enabled} onCheckedChange={(v) => setS({ ...s, online_booking_enabled: v })} /></div>
        <T k="online_booking_message" label="Message When Booking Is Off" area />
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Windshield Add-On</h3>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Enabled</Label>
          <Switch checked={!!s.windshield_addon?.enabled} onCheckedChange={(v) => setS({ ...s, windshield_addon: { ...s.windshield_addon, enabled: v } })} /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Windshield Price ($)</Label>
          <Input type="number" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.windshield_addon?.price ?? 0} onChange={(e) => setS({ ...s, windshield_addon: { ...s.windshield_addon, price: Number(e.target.value) } })} /></div>
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Extra Add-On (optional, future use)</h3>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Enabled</Label>
          <Switch data-testid="toggle-extra-addon" checked={!!s.extra_addon?.enabled} onCheckedChange={(v) => setS({ ...s, extra_addon: { ...s.extra_addon, enabled: v } })} /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Label / Name</Label>
          <Input className="rounded-none bg-black border-white/10 mt-1" value={s.extra_addon?.label ?? ""} onChange={(e) => setS({ ...s, extra_addon: { ...s.extra_addon, label: e.target.value } })} placeholder="e.g. Sunroof Tint" /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Price ($)</Label>
          <Input type="number" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.extra_addon?.price ?? 0} onChange={(e) => setS({ ...s, extra_addon: { ...s.extra_addon, price: Number(e.target.value) } })} /></div>
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Payments & Tax</h3>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Enable Online Payments</Label>
          <Switch data-testid="toggle-online-payments" checked={!!s.online_payments_enabled} onCheckedChange={(v) => setS({ ...s, online_payments_enabled: v })} /></div>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Require Deposit (kept OFF by default)</Label>
          <Switch data-testid="toggle-deposit" checked={!!s.deposit?.enabled} onCheckedChange={(v) => setS({ ...s, deposit: { ...s.deposit, enabled: v } })} /></div>
        <div><Label className="text-xs uppercase text-muted-foreground">Deposit Amount ($)</Label>
          <Input type="number" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.deposit?.amount ?? 0} onChange={(e) => setS({ ...s, deposit: { ...s.deposit, amount: Number(e.target.value) } })} /></div>
        <div className="border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Apply Sales Tax</Label>
            <Switch data-testid="toggle-tax" checked={!!s.tax?.enabled} onCheckedChange={(v) => setS({ ...s, tax: { ...s.tax, enabled: v } })} /></div>
          <div><Label className="text-xs uppercase text-muted-foreground">Tax Rate (%)</Label>
            <Input type="number" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.tax?.rate ?? 0} onChange={(e) => setS({ ...s, tax: { ...s.tax, rate: Number(e.target.value) } })} /></div>
        </div>
        <div className="border border-white/10 p-4 space-y-3">
          <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Card Surcharge (where permitted)</Label>
            <Switch checked={!!s.card_surcharge?.enabled} onCheckedChange={(v) => setS({ ...s, card_surcharge: { ...s.card_surcharge, enabled: v } })} /></div>
          <div><Label className="text-xs uppercase text-muted-foreground">Surcharge Rate (%)</Label>
            <Input type="number" className="rounded-none bg-black border-white/10 mt-1 w-40" value={s.card_surcharge?.rate ?? 0} onChange={(e) => setS({ ...s, card_surcharge: { ...s.card_surcharge, rate: Number(e.target.value) } })} /></div>
        </div>
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Old Tint Removal</h3>
        <div className="flex items-center gap-3"><Label className="text-xs uppercase text-muted-foreground">Duration Label</Label></div>
        <Input className="rounded-none bg-black border-white/10" value={s.old_tint_removal?.duration ?? ""} onChange={(e) => setS({ ...s, old_tint_removal: { ...s.old_tint_removal, duration: e.target.value } })} placeholder="Estimated 6 hours – 2 days" />
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Warranty & Care Info</h3>
        <T k="warranty_text" label="5-Year Warranty Text" area />
        <T k="curing_text" label="Curing Information" area />
        <T k="after_care_text" label="After-Installation Care" area />
      </section>

      <section className="space-y-4 border-t border-white/10 pt-6">
        <h3 className="font-head font-bold uppercase text-primary">Message Templates</h3>
        <p className="text-xs text-muted-foreground">Placeholders: {"{name} {service} {date} {time} {vehicle}"}</p>
        <T k="confirmation_message" label="Confirmation SMS" area />
        <T k="reminder_message" label="1-Hour Reminder SMS" area />
      </section>

      <Button data-testid="save-settings" onClick={save} className="rounded-none bg-primary text-primary-foreground font-bold uppercase">Save Settings</Button>
    </div>
  );
}
